import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import type { PremiseAddress, PremiseConfig } from "@shared/types/foyer";
import { isValidTimeZone } from "@shared/timezone";
import tzLookup from "tz-lookup";
import PremiseMap from "./PremiseMap";
import GooglePlacesAutocomplete from "./GooglePlacesAutocomplete";

interface Props {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    postcode?: string;
    road?: string;
    house_number?: string;
    country_code?: string;
    state?: string;
  };
}

/**
 * Address inputs + Leaflet map. The user can:
 *   - Type a city / street / freeform address → Search → Nominatim
 *     geocodes it and drops the pin. Address fields auto-fill from
 *     what Nominatim returns (best-effort - its results vary).
 *   - Click the map to set the pin manually.
 *   - Drag the pin.
 *
 * All edits mutate the editor draft via `onChange`. Saving happens at
 * the page level - the Save button in the AppBar PUTs the whole draft.
 *
 * Nominatim is free, no API key, rate-limited to ~1 req/sec per IP.
 * We don't paginate or auto-complete; a single search per click keeps
 * us safely under the limit.
 */
/**
 * The timezone this pin stands in.
 *
 * ⛔ **Derived, never typed.** It used to be an Autocomplete an operator picked
 * from, which was itself a fix for free text - "Stockholm" or a trailing space
 * used to be a 500 on the signage timeline, i.e. a BLACK lobby screen. A picker
 * removes the typo and keeps the other half of the problem: a real zone that is
 * simply the wrong one. A site running two hours out schedules everything two
 * hours out, and nothing about that looks broken until somebody stands in the
 * room at the wrong time.
 *
 * A coordinate cannot be in the wrong timezone. `tz-lookup` is an offline
 * polygon lookup (no key, no network, no quota), so this resolves while the pin
 * is still being dragged.
 *
 * Returns undefined rather than throwing: the library rejects out-of-range
 * coordinates, and a bad pin must leave the stored zone alone rather than
 * clearing it - see `patchAddress`.
 */
function zoneAt(lat: number | undefined, lng: number | undefined) {
  if (typeof lat !== "number" || typeof lng !== "number") return undefined;
  try {
    return tzLookup(lat, lng);
  } catch {
    return undefined;
  }
}

export default function PremiseLocationCard({ premise, onChange }: Props) {
  const addr: PremiseAddress = premise.address ?? {};
  const badTimezone = Boolean(addr.timezone && !isValidTimeZone(addr.timezone));
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  /**
   * The one choke point every location edit goes through - the Places pick,
   * the Nominatim search, a map click and a pin drag all land here - which is
   * why the timezone is resolved HERE and not at each of those four sites.
   */
  function patchAddress(part: Partial<PremiseAddress>) {
    const next: PremiseAddress = { ...addr, ...part };
    if (part.lat !== undefined || part.lng !== undefined) {
      const zone = zoneAt(next.lat, next.lng);
      // Only ever overwritten with a real answer. A pin dropped in the sea, or
      // cleared entirely, leaves the stored zone standing: losing a correct
      // value because a coordinate went missing would take a screen's whole
      // schedule with it.
      if (zone) next.timezone = zone;
    }
    onChange({ ...premise, address: next });
  }

  async function search() {
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    setSearchError(null);
    try {
      // `addressdetails=1` so we can pre-fill structured address fields
      // from the result.
      const url = new URL("https://nominatim.openstreetmap.org/search");
      url.searchParams.set("q", q);
      url.searchParams.set("format", "json");
      url.searchParams.set("limit", "1");
      url.searchParams.set("addressdetails", "1");
      const res = await fetch(url.toString(), {
        headers: { "Accept-Language": "en" },
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const list = (await res.json()) as NominatimResult[];
      const hit = list[0];
      if (!hit) {
        setSearchError("No matches.");
        return;
      }
      const a = hit.address ?? {};
      const cityGuess = a.city ?? a.town ?? a.village;
      patchAddress({
        lat: Number(hit.lat),
        lng: Number(hit.lon),
        streetAddress:
          [a.road, a.house_number].filter(Boolean).join(" ") ||
          addr.streetAddress,
        postalCode: a.postcode ?? addr.postalCode,
        city: cityGuess ?? addr.city,
        country: a.country_code?.toUpperCase() ?? addr.country,
        region: a.state ?? addr.region,
      });
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : String(err));
    } finally {
      setSearching(false);
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 3, borderRadius: 2, flex: "1 1 360px", minWidth: 320 }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Location
      </Typography>
      <Stack spacing={2}>
        {/* Primary: Google Places typeahead. Falls back to the Nominatim
            button below if the key isn't configured or the SDK fails. */}
        <GooglePlacesAutocomplete
          onPick={(pick) => {
            patchAddress({
              streetAddress: pick.streetAddress ?? addr.streetAddress,
              postalCode: pick.postalCode ?? addr.postalCode,
              city: pick.city ?? addr.city,
              country: pick.country ?? addr.country,
              region: pick.region ?? addr.region,
              lat: pick.lat,
              lng: pick.lng,
            });
          }}
          initialValue={addr.city ?? ""}
        />

        <Stack direction="row" spacing={1}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void search();
              }
            }}
            placeholder="Search address or city (Nominatim)"
            size="small"
            fullWidth
            inputProps={{ "data-testid": "location-search" }}
          />
          <Button
            variant="outlined"
            onClick={() => void search()}
            disabled={searching || !searchQuery.trim()}
            startIcon={
              searching ? (
                <CircularProgress size={14} />
              ) : (
                <SearchIcon fontSize="small" />
              )
            }
            data-testid="location-search-submit"
          >
            Search
          </Button>
        </Stack>
        {searchError && (
          <Typography variant="caption" color="error">
            {searchError}
          </Typography>
        )}

        <PremiseMap
          lat={addr.lat}
          lng={addr.lng}
          onPick={(lat, lng) => patchAddress({ lat, lng })}
        />

        <Stack direction="row" spacing={1}>
          <TextField
            label="Street"
            value={addr.streetAddress ?? ""}
            onChange={(e) =>
              patchAddress({ streetAddress: e.target.value || undefined })
            }
            size="small"
            sx={{ flex: 2 }}
          />
          <TextField
            label="Postal"
            value={addr.postalCode ?? ""}
            onChange={(e) =>
              patchAddress({ postalCode: e.target.value || undefined })
            }
            size="small"
            sx={{ flex: 1 }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            label="City"
            value={addr.city ?? ""}
            onChange={(e) =>
              patchAddress({ city: e.target.value || undefined })
            }
            size="small"
            sx={{ flex: 2 }}
          />
          <TextField
            label="Country"
            value={addr.country ?? ""}
            onChange={(e) =>
              patchAddress({
                country: e.target.value.toUpperCase() || undefined,
              })
            }
            size="small"
            sx={{ flex: 1 }}
            inputProps={{ maxLength: 2 }}
            placeholder="SE"
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Region / State"
            value={addr.region ?? ""}
            onChange={(e) =>
              patchAddress({ region: e.target.value || undefined })
            }
            size="small"
            sx={{ flex: 2 }}
          />
          {/*
            READ-ONLY, and that is the point.
            This was a free-text field, then a picker; both let a site run in a
            zone it is not in, which schedules everything wrong while looking
            perfectly healthy. A coordinate cannot be in the wrong timezone, so
            the pin decides and this only reports what it decided.
          */}
          <TextField
            label="Timezone"
            value={addr.timezone ?? ""}
            placeholder="Set a location to resolve"
            size="small"
            sx={{ flex: 2 }}
            data-testid="premise-timezone"
            InputProps={{ readOnly: true }}
            error={badTimezone}
            helperText={
              badTimezone
                ? `"${addr.timezone}" is not a timezone - schedules here run in UTC until the pin is set again.`
                : addr.timezone
                  ? "From the map pin"
                  : "Search an address or click the map"
            }
          />
        </Stack>
        {(addr.lat || addr.lng) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}
            >
              {addr.lat?.toFixed(5)}, {addr.lng?.toFixed(5)}
            </Typography>
            <Button
              size="small"
              onClick={() => patchAddress({ lat: undefined, lng: undefined })}
            >
              Clear pin
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
